import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { Box, Tab, TableContainer, Tabs, Paper, Table, TableHead, TableRow, TableBody, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Pagination } from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import PreviewIcon from "@mui/icons-material/Preview";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HoverIcon from "../general/HoverIcon";
import AddIcon from "@mui/icons-material/Add";
import { AuthenticationResult, AccountInfo } from "@azure/msal-browser";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

interface AdminProps {
    onWait: (wait: boolean) => void;
}

const PAGE_SIZE = 40;

interface TabConfig {
    label: string;
    createPath: string;
    isGear: boolean;
    collection?: string;
    spotType?: string;
    previewPath: string;
    editPath: string;
}

const TAB_CONFIGS: TabConfig[] = [
    { label: "Inventory", createPath: "/admin/gear/create", isGear: true, collection: "inventory", previewPath: "/inventory/", editPath: "/admin/gear/edit/" },
    { label: "Portfolio", createPath: "/admin/gear/create", isGear: true, collection: "portfolio", previewPath: "/portfolio/", editPath: "/admin/gear/edit/" },
    { label: "Personal Collection", createPath: "/admin/gear/create", isGear: true, collection: "personal", previewPath: "/personal/", editPath: "/admin/gear/edit/" },
    { label: "Restorations", createPath: "/admin/spot/create", isGear: false, spotType: "restorations", previewPath: "/restorations/", editPath: "/admin/spot/edit/" },
    { label: "ToneTeam", createPath: "/admin/spot/create", isGear: false, spotType: "toneteam", previewPath: "/toneteam/", editPath: "/admin/spot/edit/" },
    { label: "Blogs", createPath: "/admin/blog/create", isGear: false, spotType: "blog", previewPath: "/blog/", editPath: "/admin/blog/edit/" },
];

const Admin: React.FC<AdminProps> = ({onWait}) => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [authToken, setAuthToken] = useState<string>("");
    const [tabItems, setTabItems] = useState<Record<number, any[]>>({});
    const [tabPages, setTabPages] = useState<Record<number, number>>({ 0: 1, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 });
    const [tabTotals, setTabTotals] = useState<Record<number, number>>({});
    const [deleteObject, setDeleteObject] = useState<any>();
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

    const fetchTab = async (tabIndex: number, page: number, token: string) => {
        onWait(true);
        const config = TAB_CONFIGS[tabIndex];
        const offset = (page - 1) * PAGE_SIZE;
        const url = config.isGear
            ? `/api/GetGear?collection=${config.collection}&limit=${PAGE_SIZE}&offset=${offset}&show_drafts=true`
            : `/api/GetSpotlight?type=${config.spotType}&limit=${PAGE_SIZE}&offset=${offset}&show_drafts=true`;
        const response = await fetch(url, {
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
        });
        if (response.ok) {
            const result = await response.json();
            console.log("fetchTab result:", tabIndex, result);
            setTabItems(prev => ({ ...prev, [tabIndex]: result.items }));
            setTabTotals(prev => ({ ...prev, [tabIndex]: result.totalCount || 0 }));
        }
        onWait(false);
    };

    useEffect(() => {
        onWait(true);
        const firstLoad = async () => {
            await instance.initialize();
            await instance.handleRedirectPromise();
            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) {
                instance.loginRedirect({ scopes: ["User.Read"] });
            } else {
                let authResult: AuthenticationResult | null = null;
                try {
                    authResult = await instance.acquireTokenSilent({ account: accounts[0], scopes: ["User.Read"] });
                    setAuthToken(authResult.idToken);
                } catch {
                    instance.loginRedirect({ scopes: ["User.Read"] });
                }
                if (authResult) {
                    const authResp = await fetch("/api/Authorize", {
                        method: "POST",
                        headers: { "Authorization": `Bearer ${authResult.idToken}`, "Content-Type": "application/json" }
                    });
                    if (!authResp.ok) { navigate("/"); return; }
                    await fetchTab(0, 1, authResult.idToken);
                }
            }
        };
        firstLoad();
    }, []);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setSelectedTabIndex(newValue);
        if (!tabItems[newValue]) {
            fetchTab(newValue, tabPages[newValue] || 1, authToken);
        }
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        setTabPages(prev => ({ ...prev, [selectedTabIndex]: page }));
        fetchTab(selectedTabIndex, page, authToken);
    };

    const handleConfirmDelete = async () => {
        onWait(true);
        const type = (deleteObject.make) ? "gear" : "spotlight";
        const resp = await fetch(`/api/DeleteItem/${type}/${deleteObject.id}`, {
            method: "DELETE",
            headers: { "Authorization": `Bearer ${authToken}` }
        });
        if (resp.ok) {
            setTabItems(prev => ({
                ...prev,
                [selectedTabIndex]: (prev[selectedTabIndex] || []).filter((i: any) => i.id !== deleteObject.id)
            }));
            setTabTotals(prev => ({ ...prev, [selectedTabIndex]: Math.max(0, (prev[selectedTabIndex] || 1) - 1) }));
        }
        setDeleteObject(null);
        onWait(false);
    };

    const renderTable = (tabIndex: number) => {
        const config = TAB_CONFIGS[tabIndex];
        const items = tabItems[tabIndex] || [];
        const total = tabTotals[tabIndex] || 0;
        const page = tabPages[tabIndex] || 1;
        const totalPages = Math.ceil(total / PAGE_SIZE);

        const rows = items.map((item: any) => (
            <StyledTableRow key={item.path}>
                <StyledTableCell component="th" scope="row">{item.title}</StyledTableCell>
                <StyledTableCell align="right">
                    <HoverIcon Icon={PreviewIcon} onClick={() => navigate(`${config.previewPath}${item.path}`)} />
                    <HoverIcon Icon={EditIcon} onClick={() => navigate(`${config.editPath}${item.path}`)} />
                    <HoverIcon Icon={DeleteIcon} onClick={() => setDeleteObject(item)} />
                </StyledTableCell>
            </StyledTableRow>
        ));

        return (
            <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h2 style={{ margin: "5px 0px" }}>{config.label}</h2>
                    <Button onClick={() => navigate(config.createPath)} variant="outlined" startIcon={<AddIcon />}>Create</Button>
                </div>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} aria-label="customized table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Title</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{rows}</TableBody>
                    </Table>
                </TableContainer>
                {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
                    </Box>
                )}
            </>
        );
    };

    const deleteConfirmDialog = (
        <Dialog open={!!deleteObject} onClose={() => setDeleteObject(null)} aria-labelledby="confirm-delete-dialog">
            <DialogTitle id="confirm-delete-dialog">Confirm Deletion</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete this item? This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteObject(null)} color="primary">Cancel</Button>
                <Button onClick={handleConfirmDelete} color="secondary" autoFocus>Confirm</Button>
            </DialogActions>
        </Dialog>
    );

    const a11yProps = (index: number) => ({
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`
    });

    return (
        <div>
            {deleteConfirmDialog}
            <section className={"section-hero parallax--bg"}>
                <div className="container">
                    <h1 className="parallax--box">Administration</h1>
                    <p>Manage gear, restorations, events, and more</p>
                </div>
            </section>
            <Box sx={{ width: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs variant="scrollable" scrollButtons="auto" value={selectedTabIndex} onChange={handleTabChange}>
                        {TAB_CONFIGS.map((config, i) => (
                            <Tab key={i} label={config.label} {...a11yProps(i)} />
                        ))}
                    </Tabs>
                </Box>
                {TAB_CONFIGS.map((_, i) => (
                    <div key={i} hidden={selectedTabIndex !== i} style={{ padding: "20px" }}>
                        {renderTable(i)}
                    </div>
                ))}
            </Box>
        </div>
    );
}

export default Admin;